import {
  Button,
  HStack,
  IconButton,
  Image,
  Link,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useClipboard,
  useToast,
  VStack,
} from '@chakra-ui/react';
import ContentCopySharpIcon from '@gamma/ui/icons/ContentCopySharpIcon';
import DashboardSharpIcon from '@gamma/ui/icons/DashboardSharpIcon';
import DatabaseSharpIcon from '@gamma/ui/icons/DatabaseSharpIcon';
import FrameInspectSharpIcon from '@gamma/ui/icons/FrameInspectSharpIcon';
import KeyboardArrowDownSharpIcon from '@gamma/ui/icons/KeyboardArrowDownSharpIcon';
import ListAltSharpIcon from '@gamma/ui/icons/ListAltSharpIcon';
import MoreHorizSharpIcon from '@gamma/ui/icons/MoreHorizSharpIcon';
import NorthEastSharpIcon from '@gamma/ui/icons/NorthEastSharpIcon';
import { RouterLink } from '@gamma/ui/navigation';
import { useContext, useEffect, useMemo } from 'react';
import {
  EXTERNAL_SERVICE_LOGOS,
  ROUTES,
  URLS,
} from '@investigator/shared/constants';
import { i18n } from '@investigator/shared/localization';
import { AuthContext } from '@investigator/shared/providers';
import { useEntitiesContext } from '../../../../context/EntitiesContext';
import { SuppressEntityMenuItem } from '@investigator/shared/components/SuppressEntityMenuItem';
import { logScaleEntityPivot } from '@investigator/shared/utils';

import validator from 'validator';

const ipaddr = require('ipaddr.js');

const { CENSYS, SHODAN, VIRUS_TOTAL } = URLS;

const { copied, copyValue, controls, viewDetails, viewDetections } =
  i18n.pages.detections;

export interface EntityInformationActionsProps {
  end?: number;
  start?: number;
  isModal?: boolean;
  entityName?: string;
  onClose?: () => void;
  onSuppressEntity?: () => void;
}

export const EntityInformationActions = ({
  end,
  start,
  isModal,
  entityName,
  onClose,
  onSuppressEntity,
}: EntityInformationActionsProps): JSX.Element => {
  const toast = useToast();
  const { hasCopied, onCopy } = useClipboard(entityName || '');

  const { userRole } = useContext(AuthContext);

  const {
    selectedEntity,
    analysisModal: { onOpen: onEntityModalOpen },
    suppression,
  } = useEntitiesContext();

  const isSuppressed = selectedEntity?.entityIsSuppressed || false;
  const isLoading = suppression.loading || suppression.pendingUpdate;

  useEffect(() => {
    if (hasCopied) {
      toast({
        position: 'bottom-right',
        status: 'success',
        description: copied,
      });
    }
  }, [hasCopied, toast]);

  const logScaleEntityQuery = useMemo(
    () =>
      start && end && entityName
        ? logScaleEntityPivot({
            start,
            end,
            entityName,
          })
        : '',
    [end, entityName, start],
  );

  return (
    <VStack pb={!isModal ? 2 : 0} spacing={4}>
      <HStack w="100%">
        <Menu>
          <MenuButton
            as={Button}
            flexGrow={1}
            variant="solid"
            colorScheme="blue"
            size="sm"
          >
            <HStack ml={!isModal ? '102px' : 0}>
              <FrameInspectSharpIcon />
              <Text>{controls.investigate}</Text>
              <KeyboardArrowDownSharpIcon />
            </HStack>
          </MenuButton>
          <MenuList>
            {!isModal && (
              <MenuItem
                as={Link}
                _hover={{
                  textDecoration: 'none',
                }}
                onClick={() => onEntityModalOpen(selectedEntity)}
              >
                <HStack>
                  <DashboardSharpIcon />
                  <Text>{viewDetails}</Text>
                </HStack>
              </MenuItem>
            )}
            <MenuItem
              minW={!isModal ? '335px' : 'auto'}
              target="_blank"
              as={RouterLink}
              icon={<DatabaseSharpIcon />}
              to={{
                pathname: ROUTES.logSearch,
                search: logScaleEntityQuery,
              }}
            >
              <HStack>
                <Text>{controls.viewIpLogs}</Text>
                <NorthEastSharpIcon />
              </HStack>
            </MenuItem>
            <MenuItem
              as={RouterLink}
              onClick={onClose}
              icon={<ListAltSharpIcon />}
              to={{
                pathname: ROUTES.detections,
                search: `start=${start}&end=${end}&source_name=${entityName}`,
              }}
            >
              {viewDetections}
            </MenuItem>
          </MenuList>
        </Menu>
        <Menu offset={[isModal ? -192 : -199, 8]}>
          <Tooltip label={controls.moreActions}>
            <MenuButton
              as={IconButton}
              variant="solid"
              colorScheme="gray"
              isLoading={isLoading}
              size="sm"
            >
              <HStack justifyContent="center">
                <MoreHorizSharpIcon />
              </HStack>
            </MenuButton>
          </Tooltip>
          <MenuList>
            {userRole !== 'viewer' && onSuppressEntity && (
              <SuppressEntityMenuItem
                isSuppressed={isSuppressed}
                isLoading={isLoading}
                isDisabled={!suppression.enabled}
                onClick={onSuppressEntity}
                suppressText={controls.suppressEntity}
                unsuppressText={controls.unSuppressEntity}
              />
            )}
            <MenuItem onClick={onCopy} icon={<ContentCopySharpIcon />}>
              {copyValue}
            </MenuItem>
            {isModal &&
              entityName &&
              validator.isIP(entityName) &&
              ipaddr.parse(entityName)?.range() !== 'private' && (
                <>
                  <MenuDivider />
                  <MenuItem
                    as={Link}
                    isExternal
                    href={`${VIRUS_TOTAL}/${entityName}`}
                    _hover={{ textDecoration: 'none' }}
                    icon={
                      <Image
                        w="16px"
                        h="16px"
                        src={EXTERNAL_SERVICE_LOGOS.VIRUS_TOTAL}
                      />
                    }
                  >
                    <HStack pl={1}>
                      <Text>VirusTotal</Text>
                      <NorthEastSharpIcon />
                    </HStack>
                  </MenuItem>
                  <MenuItem
                    as={Link}
                    isExternal
                    href={`${SHODAN}/${entityName}`}
                    _hover={{ textDecoration: 'none' }}
                    icon={
                      <Image
                        w="16px"
                        h="16px"
                        src={EXTERNAL_SERVICE_LOGOS.SHODAN}
                      />
                    }
                  >
                    <HStack pl={1}>
                      <Text>Shodan</Text>
                      <NorthEastSharpIcon />
                    </HStack>
                  </MenuItem>
                  <MenuItem
                    as={Link}
                    isExternal
                    href={`${CENSYS}/${entityName}`}
                    _hover={{ textDecoration: 'none' }}
                    icon={
                      <Image
                        w="16px"
                        h="16px"
                        src={EXTERNAL_SERVICE_LOGOS.CENSYS}
                      />
                    }
                  >
                    <HStack pl={1}>
                      <Text>Censys</Text>
                      <NorthEastSharpIcon />
                    </HStack>
                  </MenuItem>
                </>
              )}
          </MenuList>
        </Menu>
      </HStack>
      {!isModal &&
        entityName &&
        validator.isIP(entityName) &&
        ipaddr.parse(entityName)?.range() !== 'private' && (
          <HStack w="100%">
            <Button
              as={Link}
              isExternal
              flexGrow={1}
              size="sm"
              variant="ghost"
              colorScheme="gray"
              textAlign="center"
              href={`${VIRUS_TOTAL}/${entityName}`}
              css={{ textDecoration: 'none !important' }}
            >
              <HStack>
                <Image
                  w="13px"
                  h="13px"
                  src={EXTERNAL_SERVICE_LOGOS.VIRUS_TOTAL}
                />
                <Text color="text.primary">VirusTotal</Text>
                <NorthEastSharpIcon />
              </HStack>
            </Button>
            <Button
              as={Link}
              isExternal
              flexGrow={1}
              size="sm"
              variant="ghost"
              colorScheme="gray"
              textAlign="center"
              href={`${SHODAN}/${entityName}`}
              css={{ textDecoration: 'none !important' }}
            >
              <HStack>
                <Image w="16px" h="16px" src="/assets/images/logo-shodan.png" />
                <Text color="text.primary">Shodan</Text>
                <NorthEastSharpIcon />
              </HStack>
            </Button>
            <Button
              as={Link}
              isExternal
              flexGrow={1}
              size="sm"
              variant="ghost"
              colorScheme="gray"
              textAlign="center"
              href={`${CENSYS}/${entityName}`}
              css={{ textDecoration: 'none !important' }}
            >
              <HStack>
                <Image w="16px" h="16px" src={EXTERNAL_SERVICE_LOGOS.CENSYS} />
                <Text color="text.primary">Censys</Text>
                <NorthEastSharpIcon />
              </HStack>
            </Button>
          </HStack>
        )}
    </VStack>
  );
};
